import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import FormInputs from "../../components/UI/FormInputs";
import PatientFormService from "../../services/PatientFormService";
import UnifiedSessionService from "../../services/UnifiedSessionService";
import { applyPatientSessionAuthFromApiResponse } from "../../utils/patientSessionAuth";
import { isObject, isSet } from "../../utils/commonUtils";
import { OK } from "../../utils/constant";
import { peekUnifiedIntakeJustCompleted } from "../../utils/unifiedIntakeConstants";

/**
 * 1) Token + employee_id → patient-unified-login (no patient form UI / PII yet).
 * 2) PatientFormLogin-style OTP (patient-form-login) using patient_form_id from step 1.
 * 3) Navigate to /patient_form with unified_token for manifest-driven intake + questionnaires.
 */
const UnifiedLoginScreen = ({
  token,
  employeeId,
  continueAfterCompletion = false,
  otpOnlyMode = false,
  knownPatientFormId = null,
  onLoginError,
  Loader,
  Toast,
  setSessionToken,
  setSessionData,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [phase, setPhase] = useState("securing");
  const [preAuthError, setPreAuthError] = useState("");
  const [preAuth, setPreAuth] = useState(null);
  const [formData, setFormData] = useState({});
  const [validation, setValidation] = useState({});
  const [otpStep, setOtpStep] = useState(1);
  const [isValidForm, setIsValidForm] = useState(true);
  const [phoneChannel, setPhoneChannel] = useState(true);
  const secureRanKeyRef = useRef(null);
  const validatedFormIdRef = useRef(null);

  const secureKey = `${token}|${continueAfterCompletion}|${otpOnlyMode}|${knownPatientFormId || ""}|${employeeId || ""}`;

  useEffect(() => {
    if (secureRanKeyRef.current === secureKey) {
      return;
    }
    secureRanKeyRef.current = secureKey;

    let cancelled = false;
    setPhase("securing");

    const secure = async () => {
      if (otpOnlyMode && knownPatientFormId) {
        try {
          sessionStorage.removeItem("unified_continue_anyway_flow");
          sessionStorage.removeItem("unified_packet_just_completed");
        } catch {
          /* ignore */
        }
        setPreAuth({
          patient_form_id: knownPatientFormId,
          review_mode: false,
        });
        setPhase("otp");
        return;
      }
      Loader(true);
      const payload = { token };
      if (employeeId) {
        payload.employee_id = employeeId;
      }
      if (continueAfterCompletion) {
        payload.continue_after_completion = true;
      }
      const loginResult = await UnifiedSessionService.login(payload);
      Loader(false);
      if (cancelled) return;
      if (!loginResult) {
        setPreAuthError("Unable to verify this link.");
        setPhase("error");
        return;
      }
      if (loginResult.code === 403) {
        secureRanKeyRef.current = null;
        onLoginError?.("already completed");
        return;
      }
      if (loginResult.code === 410) {
        onLoginError?.("expired");
        return;
      }
      if (loginResult.code === 401) {
        setPreAuthError("This link is not valid for the provided employee.");
        setPhase("error");
        return;
      }
      if (loginResult.code !== OK) {
        setPreAuthError("Unable to verify this link.");
        setPhase("error");
        return;
      }
      const data = loginResult.data;
      if (!data?.patient_form_id) {
        setPreAuthError("Session could not be started. Please contact your clinic.");
        setPhase("error");
        return;
      }
      try {
        if (continueAfterCompletion) {
          sessionStorage.setItem("unified_continue_anyway_flow", "1");
        } else {
          sessionStorage.removeItem("unified_continue_anyway_flow");
          sessionStorage.removeItem("unified_packet_just_completed");
        }
      } catch {
        /* ignore */
      }
      setSessionToken(null);
      setSessionData({
        stepManifest: data.step_manifest,
        currentStep: data.current_step,
        totalSteps: data.total_steps,
        patientFormId: data.patient_form_id,
        visitId: data.visit_id,
        employeeId: data.employee_id,
        organization_id: data.organization_id,
        reviewMode: !!data.review_mode,
      });
      setPreAuth(data);
      setPhase("otp");
    };
    secure();
    return () => {
      cancelled = true;
    };
    // Intentionally omit Loader / setSessionData — new references each render caused infinite API calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secureKey]);

  useEffect(() => {
    if (phase !== "otp" || !preAuth?.patient_form_id) return;
    if (validatedFormIdRef.current === preAuth.patient_form_id) return;
    validatedFormIdRef.current = preAuth.patient_form_id;

    let cancelled = false;
    const validate = async () => {
      Loader(true);
      const response = await PatientFormService.validatePatientForm(preAuth.patient_form_id, {
        reentryMode: continueAfterCompletion || !!preAuth.reentry_mode || !!preAuth.review_mode,
      });
      Loader(false);
      if (cancelled) return;
      if (!response || typeof response !== "object" || !("is_valid" in response)) {
        setIsValidForm(false);
        return;
      }
      const formCompleted =
        response.form_status?.toLowerCase() === "completed"
      if (response.is_valid === false && formCompleted && !continueAfterCompletion) {
        secureRanKeyRef.current = null;
        onLoginError?.("already completed");
        return;
      }
      setIsValidForm(response.is_valid !== false);
    };
    validate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, preAuth?.patient_form_id]);

  const changeHandler = (fieldName, value) => {
    setValidation({ ...validation, [fieldName]: "" });
    setFormData({ ...formData, [fieldName]: value });
  };

  const submitOtp = async (e) => {
    e?.preventDefault?.();
    if (!preAuth?.patient_form_id) return;
    const payload = {
      isPhoneValidation: phoneChannel,
      step: otpStep,
      patient_form_id: preAuth.patient_form_id,
      phone: formData.phone || "",
      email: formData.email || "",
      code: formData.code || "",
    };
    Loader(true);
    const data = await PatientFormService.patientFormLogin(payload);
    Loader(false);
    if (data?.status === "ERROR") {
      let updateValidation = { ...validation };
      if (isObject(data.data)) {
        for (let key in data.data) {
          updateValidation = { ...updateValidation, [key]: data.data[key].join(",") };
        }
      } else {
        Toast.error(data.data);
      }
      if (Object.keys(updateValidation).length > 0) setValidation(updateValidation);
      return;
    }
    if (otpStep === 2) {
      if (data?.code !== OK) {
        Toast.error(typeof data?.data === "string" ? data.data : "Verification failed.");
        return;
      }
      const applied = await applyPatientSessionAuthFromApiResponse(data, dispatch);
      if (!applied) {
        Toast.error("Verification succeeded but session tokens were not returned. Try again or contact support.");
        return;
      }
      const reentry = preAuth.reentry_mode || preAuth.review_mode || continueAfterCompletion;
      if (reentry) {
        sessionStorage.setItem("unified_reentry", "1");
        if (preAuth.step_manifest?.length) {
          sessionStorage.setItem("unified_reentry_manifest", JSON.stringify(preAuth.step_manifest));
        }
      }
      const reentryQs = reentry ? "&reentry=1" : "";
      const thankYou = peekUnifiedIntakeJustCompleted() ? "&thank_you_land=1" : "";
      navigate(
        `/patient_form?patient_form_id=${encodeURIComponent(preAuth.patient_form_id)}&unified_token=${encodeURIComponent(token)}${reentryQs}${thankYou}`,
        { replace: true }
      );
    } else {
      setOtpStep(otpStep + 1);
    }
  };

  if (phase === "securing") {
    return (
      <div className="sh_login">
        <div className="sh_login-bleed">
          <main className="sh_login-bg">
            <div className="container sh_login-container py-5 text-center text-muted">Securing your link…</div>
          </main>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="sh_login">
        <div className="sh_login-bleed">
          <main className="sh_login-bg">
            <div className="container sh_login-container">
              <div className="alert alert-danger mt-5">{preAuthError}</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!isValidForm) {
    return (
      <div className="sh_login">
        <div className="sh_login-bleed">
          <main className="sh_login-bg">
            <div className="container sh_login-container">
              <div className="alert alert-warning mt-5">
                This patient form link is no longer active. Please contact your clinic for a new link.
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="sh_login">
      <div className="sh_login-bleed">
        <main className="sh_login-bg">
          <div className="container sh_login-container">
            <div className="row justify-content-center">
              <div className="col-12">
                <div className="sh_login-hero text-center">
                  <div className="sh_login-brand">SnapEHR</div>
                  <div className="sh_login-title">A New Generation Has Arrived</div>
                  <div className="sh_login-subtitle">
                    Healthcare technology finally built for the way modern medicine works.
                  </div>
                </div>

                <div className="d-flex justify-content-center">
                  <div className="sh_login-card">
                    <div className="sh_login-card-inner">
                      <div className="sh_login-card-title">Verification</div>
                      <div className="sh_login-card-subtitle">
                        Confirm your identity before your forms are shown
                      </div>

                      {otpStep === 1 && (
                        <div className="d-flex align-items-center gap-3 mb-2 mt-2 text-dark">
                          {["sms", "email"].map((item, index) => (
                            <div className="d-flex align-items-center mr-3 mb-0" key={item}>
                              <FormInputs
                                id={`unified_otp_${item}_${index}`}
                                type="Radio"
                                name="unified_otp_channel"
                                className="cursor-pointer"
                                value={(item === "sms" && phoneChannel) || (item === "email" && !phoneChannel)}
                                changeHandler={() => setPhoneChannel(item === "sms")}
                              />
                              <label
                                htmlFor={`unified_otp_${item}_${index}`}
                                className="mb-0 ml-1 text-capitalize font-weight-bold d-inline-flex align-items-center"
                              >
                                {item}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}

                      <form className="mt-3" onSubmit={submitOtp}>
                        {otpStep === 1 ? (
                          <>
                            <FormInputs
                              type={phoneChannel ? "Phone" : ""}
                              className="form-control form-control-fields sh_login-control"
                              placeholder={phoneChannel ? "Enter Phone Number" : "Enter Email Address"}
                              name={phoneChannel ? "phone" : "email"}
                              changeHandler={(value) => changeHandler(phoneChannel ? "phone" : "email", value)}
                              value={formData[phoneChannel ? "phone" : "email"]}
                            />
                            {isSet(validation[phoneChannel ? "phone" : "email"], "") !== "" ? (
                              <span className="text-danger m-1">
                                {isSet(validation[phoneChannel ? "phone" : "email"], "")}
                              </span>
                            ) : null}
                            <div className="d-flex align-items-start mt-3 mb-2 text-muted">
                              <i className="fas fa-info-circle pt-2 fa-lg" />
                              <p className="font-weight-normal text-sm pl-2 mb-0">
                                {`We will send a verification code to your ${phoneChannel ? "phone number" : "email address"}.`}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <FormInputs
                              type=""
                              className="form-control form-control-fields sh_login-control"
                              placeholder="Enter Code"
                              name="code"
                              changeHandler={(value) => changeHandler("code", value)}
                              value={formData.code}
                            />
                            {isSet(validation.code, "") !== "" ? (
                              <span className="text-danger m-1">{isSet(validation.code, "")}</span>
                            ) : null}
                            <div className="d-flex align-items-start mt-3 text-secondary">
                              <i className="fas fa-info-circle pt-2 fa-lg" />
                              <p className="font-weight-normal text-sm pl-2 mb-0">
                                Enter the code we sent to continue to your forms.
                              </p>
                            </div>
                          </>
                        )}

                        <button type="submit" className="btn sh_login-btn w-100 mt-2">
                          Submit
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default UnifiedLoginScreen;
